/**
 * O prompt de reautenticacao.
 *
 * **Por que a API responde 403 e nao 401.** A sessao existe e vale para tudo o
 * mais; o que falta e a segunda prova. A API sinaliza isso com o header
 * `X-requer-reauth`, e a tela abre o prompt em vez de mandar a pessoa logar de
 * novo -- que seria perder o trabalho que ela estava fazendo.
 *
 * **O prompt e uma promessa.** A acao que o disparou fica esperando, e volta a
 * rodar depois que a pessoa confirma. Sem isso, a tela teria de repetir a
 * acao sozinha em cada botao, e cada botao esqueceria de uma.
 */

import { ErroDaApi, reautenticar } from "./api";
import { telaDeReautenticacao } from "./telas";

const CONTAINER = "cobertor-reauth";

/**
 * Pede a senha e resolve `true` quando a pessoa confirmou.
 *
 * **Resolve `false` quando cancela**, e nao lanca: cancelar uma acao nao e um
 * erro, e quem chamou decide o que fazer com isso.
 */
export function pedirReautenticacao(motivo: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const area = document.createElement("div");
    area.id = CONTAINER;
    area.innerHTML = telaDeReautenticacao(motivo);
    document.body.appendChild(area);

    const erro = area.querySelector<HTMLElement>("#erro-reauth");
    const botao = area.querySelector<HTMLButtonElement>("#confirmar-reauth");
    const forma = area.querySelector<HTMLFormElement>("#forma-reauth");
    const cancelar = area.querySelector<HTMLButtonElement>("#cancelar-reauth");

    const fecha = (resposta: boolean): void => {
      area.remove();
      resolve(resposta);
    };

    cancelar?.addEventListener("click", () => fecha(false));

    forma?.addEventListener("submit", (evento) => {
      evento.preventDefault();
      if (botao !== null) {
        botao.disabled = true;
      }
      const senha = area.querySelector<HTMLInputElement>("#senha-reauth")?.value ?? "";
      const codigo = area.querySelector<HTMLInputElement>("#codigo-reauth")?.value.trim() ?? "";
      void reautenticar(senha, codigo === "" ? null : codigo)
        .then(() => {
          fecha(true);
        })
        .catch((problema: unknown) => {
          if (erro !== null) {
            erro.hidden = false;
            erro.textContent =
              problema instanceof ErroDaApi ? problema.message : "Nao deu para confirmar.";
          }
          if (botao !== null) {
            botao.disabled = false;
          }
        });
    });
  });
}

/**
 * Roda uma acao, e se a API pedir reautenticacao, pede e **repete**.
 *
 * **A repeticao e o ponto.** A acao e a mesma funcao, chamada de novo: nao ha
 * "tentar de novo" espalhado por cada botao, e nao ha o risco de um botao
 * repetir a acao errada. E a acao precisa ser **idempotente por definicao** --
 * no maximo uma repeticao acontece, porque a segunda resposta vem sem o 403.
 */
export async function comReautenticacao(acao: () => Promise<void>): Promise<void> {
  try {
    await acao();
  } catch (erro) {
    if (erro instanceof ErroDaApi && erro.requerReautenticacao) {
      const confirmou = await pedirReautenticacao(erro.message);
      if (confirmou) {
        await acao();
        return;
      }
    }
    throw erro;
  }
}
